// Collective Intelligence consent (T1 content-free, T2 content-derived): two separate, optional choices,
// off by default, recorded against the exact text version shown, and always withdrawable here.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Users, ChevronRight, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CI_COPY = {
  en: {
    row: 'Help improve Arvexis',
    off: 'Off · optional',
    t1on: 'Content-free signals on',
    t2on: 'Message patterns on',
    both: 'Signals and message patterns on',
    title: 'Help improve Arvexis',
    intro: 'Both choices are optional and stay off unless you turn them on. Arvexis works exactly the same for you either way, and you can change your mind here at any time.',
    t1: {
      name: 'Share how you use Arvexis',
      tag: 'Content-free',
      body: 'When you tap helpful or not helpful, copy a reply or listen to one, Arvexis may count that tap, with its time and your language, together with many other members. Nothing you write or say is included, and the count cannot be linked back to a conversation or a message.',
    },
    t2: {
      name: 'Learn from the shape of your messages',
      tag: 'Separate permission',
      body: 'Arvexis may note simple, fixed features of what you write in Message, such as whether a message is short or long, or a question. Your words themselves are never stored for this program, shared through it, or used to train any AI model. Messages that mention health, money, the law, beliefs, contact details or anything safety-related are skipped entirely. Talk and Dreams are never included.',
    },
    shared: 'Only patterns shared by at least 25 members are ever kept, and a person reviews each one before anything about Arvexis changes. Turning a choice off removes what was kept from you; deleting your account removes it too.',
    agree: 'I have read this and agree',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    since: 'On since',
    offNote: 'Off',
    error: "We couldn't save that. Please try again.",
    close: 'Close',
    invite: {
      eyebrow: 'Optional · off unless you choose',
      headline: 'Help Arvexis grow wiser, if you want to',
      body: 'Two separate choices. Say yes to one, both or neither. Arvexis is exactly the same for you either way, and you can change your mind any time in Settings.',
      notNow: 'Not now',
      done: 'Continue',
    },
  },
  es: {
    row: 'Ayuda a mejorar Arvexis',
    off: 'Desactivado · opcional',
    t1on: 'Señales sin contenido activadas',
    t2on: 'Patrones de mensajes activados',
    both: 'Señales y patrones de mensajes activados',
    title: 'Ayuda a mejorar Arvexis',
    intro: 'Ambas opciones son voluntarias y permanecen desactivadas salvo que tú las actives. Arvexis funciona exactamente igual para ti en cualquier caso, y puedes cambiar de opinión aquí cuando quieras.',
    t1: {
      name: 'Compartir cómo usas Arvexis',
      tag: 'Sin contenido',
      body: 'Cuando marcas útil o no útil, copias una respuesta o la escuchas, Arvexis puede contar ese toque, con su hora y tu idioma, junto con los de muchos otros miembros. No se incluye nada de lo que escribes o dices, y el recuento no puede vincularse a una conversación ni a un mensaje.',
    },
    t2: {
      name: 'Aprender de la forma de tus mensajes',
      tag: 'Permiso aparte',
      body: 'Arvexis puede anotar rasgos simples y fijos de lo que escribes en Message, como si un mensaje es corto o largo, o si es una pregunta. Tus palabras en sí nunca se guardan para este programa, ni se comparten a través de él, ni se usan para entrenar ningún modelo de IA. Los mensajes que mencionan salud, dinero, temas legales, creencias, datos de contacto o cualquier asunto de seguridad se omiten por completo. Talk y Dreams nunca se incluyen.',
    },
    shared: 'Solo se conservan patrones compartidos por al menos 25 miembros, y una persona revisa cada uno antes de que cambie nada en Arvexis. Desactivar una opción elimina lo que se conservó de ti; eliminar tu cuenta también lo elimina.',
    agree: 'Lo he leído y estoy de acuerdo',
    turnOn: 'Activar',
    turnOff: 'Desactivar',
    since: 'Activado desde',
    offNote: 'Desactivado',
    error: 'No pudimos guardarlo. Inténtalo de nuevo.',
    close: 'Cerrar',
    invite: {
      eyebrow: 'Opcional · desactivado salvo que tú elijas',
      headline: 'Ayuda a Arvexis a ser más sabia, si quieres',
      body: 'Dos opciones independientes. Acepta una, las dos o ninguna. Arvexis es exactamente igual para ti en cualquier caso, y puedes cambiar de opinión cuando quieras en Ajustes.',
      notNow: 'Ahora no',
      done: 'Continuar',
    },
  },
};

const summary = (c, s) => {
  const a = s?.t1?.enabled, b = s?.t2?.enabled;
  return a && b ? c.both : a ? c.t1on : b ? c.t2on : c.off;
};

export const TierCard = ({ tier, c, state, lang, busy, onChange }) => {
  const [agreed, setAgreed] = useState(false);
  const on = !!state?.enabled;
  const copy = c[tier];
  const since = on && state.changed_at
    ? new Date(state.changed_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { dateStyle: 'medium' }) : null;
  return (
    <section data-testid={`ci-consent-${tier}`} className="rounded-2xl p-5"
      style={{ border: `1px solid ${on ? 'rgba(232,197,143,0.45)' : 'rgba(244,239,231,0.12)'}`,
        background: on ? 'rgba(232,197,143,0.06)' : 'rgba(255,255,255,0.02)',
        transition: 'border-color 200ms ease, background-color 200ms ease' }}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-base font-semibold" style={{ color: '#F4EFE7' }}>{copy.name}</h3>
        <span className="text-[11px] uppercase tracking-[0.14em] px-2 py-1 rounded-full"
          style={{ color: '#E8C58F', border: '1px solid rgba(232,197,143,0.35)' }}>{copy.tag}</span>
      </div>
      <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(244,239,231,0.72)' }}>{copy.body}</p>
      {on ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs" data-testid={`ci-consent-${tier}-status`} style={{ color: '#E8C58F' }}>
            {since ? `${c.since} ${since}` : c.turnOn}
          </span>
          <button type="button" disabled={busy} onClick={() => onChange(tier, false)}
            data-testid={`ci-consent-${tier}-withdraw`}
            className="px-4 py-2 rounded-full text-sm disabled:opacity-50"
            style={{ color: '#F4EFE7', border: '1px solid rgba(244,239,231,0.25)' }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : c.turnOff}
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-start gap-2 text-sm cursor-pointer" style={{ color: 'rgba(244,239,231,0.85)' }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5"
              data-testid={`ci-consent-${tier}-agree`} />
            <span>{c.agree}</span>
          </label>
          <span className="sr-only" data-testid={`ci-consent-${tier}-status`}>{c.offNote}</span>
          <button type="button" disabled={!agreed || busy} onClick={() => onChange(tier, true)}
            data-testid={`ci-consent-${tier}-enable`}
            className="px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-40"
            style={{ background: '#E8C58F', color: '#0B0B12', transition: 'opacity 160ms ease' }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : c.turnOn}
          </button>
        </div>
      )}
    </section>
  );
};

export const useCollectiveConsent = (c, channel = 'settings') => {
  const [state, setState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => axios.get(`${API}/collective/consent`).then((r) => setState(r.data)).catch(() => {})
    .finally(() => setLoaded(true)), []);
  useEffect(() => { load(); }, [load]);

  const change = async (tier, enabled, extra = {}) => {
    setBusy(tier); setError('');
    try {
      const r = await axios.post(`${API}/collective/consent`,
        { tier, enabled, version: state?.[tier]?.version || '', channel, ...extra });
      setState(r.data);
      return true;
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : c.error);
      load();
      return false;
    } finally { setBusy(null); }
  };
  return { state, loaded, busy, error, change };
};

export const CollectiveConsentRow = ({ lang }) => {
  const c = CI_COPY[lang === 'es' ? 'es' : 'en'];
  const { state, busy, error, change } = useCollectiveConsent(c);
  const [open, setOpen] = useState(false);

  if (!state || !(state.available || state.t1?.enabled || state.t2?.enabled)) return null;
  return (
    <>
      <button className="settings-row" onClick={() => setOpen(true)} data-testid="ci-consent-row"
        style={{ width: '100%', textAlign: 'left' }}>
        <div className="settings-row__icon"><Users className="w-5 h-5" /></div>
        <div className="settings-row__content">
          <span className="settings-row__label">{c.row}</span>
          <span className="settings-row__subtitle" data-testid="ci-consent-row-summary">{summary(c, state)}</span>
        </div>
        <ChevronRight className="settings-row__chevron" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ci-consent-sheet" className="max-w-lg max-h-[90vh] overflow-y-auto border-0 p-6 sm:p-8 [&>button]:hidden"
          style={{ background: '#0F0E14', color: '#F4EFE7', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' }}>
          <div className="flex items-start justify-between gap-4">
            <DialogTitle className="text-2xl font-semibold" style={{ color: '#F4EFE7' }}>{c.title}</DialogTitle>
            <button type="button" onClick={() => setOpen(false)} aria-label={c.close} data-testid="ci-consent-close"
              className="p-1 rounded-full" style={{ color: 'rgba(244,239,231,0.6)' }}><X className="w-5 h-5" /></button>
          </div>
          <DialogDescription className="text-sm leading-relaxed" style={{ color: 'rgba(244,239,231,0.7)' }}>{c.intro}</DialogDescription>
          <div className="flex flex-col gap-4 mt-2">
            {['t1', 't2'].map((tier) => (
              <TierCard key={`${tier}-${state[tier]?.enabled}`} tier={tier} c={c} lang={lang} state={state[tier]}
                busy={busy === tier} onChange={change} />
            ))}
          </div>
          <p className="text-xs leading-relaxed mt-2" style={{ color: 'rgba(244,239,231,0.55)' }}
            data-testid="ci-consent-shared-note">{c.shared}</p>
          {error && <p className="text-sm mt-1" data-testid="ci-consent-error" style={{ color: '#F2A7A7' }}>{error}</p>}
        </DialogContent>
      </Dialog>
    </>
  );
};
